import {
  AllInclusive as AllInclusiveIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { Box, Card, Chip, Stack, Tab, Tabs, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import HttpServices from 'src/services/httpService';
import { paymentModuleUrl, userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import BillingComponent from './billing.component';
import CustomLifetimePlanComponent from './custom-lifetime-plan.component';
import DefaultPlansComponent from './default-plans.component';
//import LifetimePlansComponent from './lifetime-plans.component';

const hasLifetimePlan = (planDetails) => {
  const planNames = `${planDetails?.default_plan_name || ''} ${planDetails?.paid_plan_name || ''}`.toLowerCase();
  return Boolean(planDetails?.is_appsumo_user || planNames.includes('lifetime'));
};

const CUSTOM_LIFETIME_TAB_EMAILS = ['harikrrish27@gmail.com'];
const SHOW_CUSTOM_LIFETIME_PLANS = false;

const BillingInfoComponent = () => {
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const userPermissions = CommonHelper.GetBrandInfoFromLocalStorage('permissions');
  const userDetails = CommonHelper.GetBrandInfoFromLocalStorage('user_details') || {};

  const [currentTab, setCurrentTab] = useState('Subscription Info');
  const [currentPlanDetails, setCurrentPlanDetails] = useState(null);
  const [customLifetimeRequest, setCustomLifetimeRequest] = useState(null);
  const isLifetimeEnabled = hasLifetimePlan(currentPlanDetails);
  const isCustomLifetimeAdmin = SHOW_CUSTOM_LIFETIME_PLANS && CUSTOM_LIFETIME_TAB_EMAILS.includes(String(userDetails?.email || '').trim().toLowerCase());
  const hasCustomLifetimeRequest = SHOW_CUSTOM_LIFETIME_PLANS && Boolean(customLifetimeRequest?.id && customLifetimeRequest?.status !== 'paid' && !isLifetimeEnabled);
  const hasPayableCustomLifetimeRequest = Boolean(hasCustomLifetimeRequest && customLifetimeRequest?.can_pay);
  const canViewCustomLifetimeTab = isCustomLifetimeAdmin || hasCustomLifetimeRequest;
  const isBillingLockMode = CommonHelper.ShouldBlockAppForBilling();

  const getCurrentSubscription = useCallback(() => {
    HttpServices.Get({
      url: userModuleURL.getCurrentSubscription,
      successCallback: (data) => setCurrentPlanDetails(data),
      failureCallback: (message) => message && showToast(message, 'e'),
    });
  }, [showToast]);

  useEffect(() => {
    getCurrentSubscription();
  }, [getCurrentSubscription]);

  useEffect(() => {
    if (!SHOW_CUSTOM_LIFETIME_PLANS) return;
    HttpServices.Get({
      url: paymentModuleUrl.customLifetimeRequest,
      successCallback: (data) => setCustomLifetimeRequest(data?.id ? data : null),
      failureCallback: () => setCustomLifetimeRequest(null),
    });
  }, []);

  useEffect(() => {
    if (isBillingLockMode) {
      const targetTab = hasCustomLifetimeRequest ? 'Custom lifetime plan info' : 'Lifetime plan info';
      if (currentTab !== targetTab) {
        setCurrentTab(targetTab);
      }
      return;
    }
    if (!canViewCustomLifetimeTab && currentTab === 'Custom lifetime plan info') {
      setCurrentTab('Subscription Info');
      return;
    }
    if (hasCustomLifetimeRequest && currentTab === 'Lifetime plan info') {
      setCurrentTab('Custom lifetime plan info');
      return;
    }
    //customlifetime plan request
    // if (isLifetimeEnabled && !isCustomLifetimeAdmin && currentTab !== 'Subscription Info') {
    //   setCurrentTab('Subscription Info');
    // }
  }, [canViewCustomLifetimeTab, currentTab, hasCustomLifetimeRequest, isBillingLockMode, isCustomLifetimeAdmin, isLifetimeEnabled]);

  const handleBuyPlan = () => {
    if (hasCustomLifetimeRequest) {
      setCurrentTab('Custom lifetime plan info');
      return;
    }
    //customlifetime plan request
    //  if (!isLifetimeEnabled) {
    //   setCurrentTab('Lifetime plan info');
    // }
    setCurrentTab('Lifetime plan info');
  };

  const ACCOUNT_TABS = [
    {
      value: 'Subscription Info',
      icon: <BusinessIcon sx={{ width: '20px', height: '20px' }} />,
      component: <BillingComponent handleBuyPlan={handleBuyPlan} />,
      permissions: [CommonHelper.PermissionList.ORG_ADMIN],
      label: translate('billing.subscription_info.title'),
    },
    // customlifetimeplan
    //  {
    //   value: 'Lifetime plan info',
    //   icon: <AllInclusiveIcon sx={{ width: '20px', height: '20px' }} />,
    //   component: <LifetimePlansComponent />,
    //   permissions: [CommonHelper.PermissionList.ORG_ADMIN],
    //   label: translate('billing.lifetime_plan_info.title'),
    //   hide: isLifetimeEnabled || hasCustomLifetimeRequest,
    // },
    {
      value: 'Lifetime plan info',
      icon: <AllInclusiveIcon sx={{ width: '20px', height: '20px' }} />,
      component: <DefaultPlansComponent currentPlanDetails={currentPlanDetails} />,
      permissions: [CommonHelper.PermissionList.ORG_ADMIN],
      label: translate('billing.lifetime_plan_info.title'),
      hide: hasCustomLifetimeRequest,
    },
    {
      value: 'Custom lifetime plan info',
      icon: <AllInclusiveIcon sx={{ width: '20px', height: '20px' }} />,
      component: (
        <CustomLifetimePlanComponent
          initialRequest={customLifetimeRequest}
          isAdmin={isCustomLifetimeAdmin}
          isPaymentOnly={!isCustomLifetimeAdmin && hasCustomLifetimeRequest}
          onRequestSaved={setCustomLifetimeRequest}
        />
      ),
      permissions: [CommonHelper.PermissionList.ORG_ADMIN],
      label: hasPayableCustomLifetimeRequest && !isCustomLifetimeAdmin
        ? translate('billing.custom_lifetime_plan_info.payment_title')
        : translate('billing.custom_lifetime_plan_info.title'),
      hide: !canViewCustomLifetimeTab,
    },
  ];

  if (isBillingLockMode) {
    const paymentComponent = hasCustomLifetimeRequest ? (
      <CustomLifetimePlanComponent
        initialRequest={customLifetimeRequest}
        isAdmin={isCustomLifetimeAdmin}
        isPaymentOnly={!isCustomLifetimeAdmin && hasCustomLifetimeRequest}
        onRequestSaved={setCustomLifetimeRequest}
      />
    ) : (
     //  <LifetimePlansComponent />
      <DefaultPlansComponent currentPlanDetails={currentPlanDetails} />
    );

    return (
      <>
        <Helmet>
          <title>{translate('billing.title')}</title>
        </Helmet>
      <Box
        sx={{
          width: '100%',
          px: { xs: 1.5, md: 2 },
          pt: { xs: 3, md: 4 },
          pb: { xs: 1.5, md: 2 },
        }}
      >
          <Card
            sx={{
              p: { xs: 2, md: 2.5 },
              mb: 2,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderLeft: `4px solid ${theme.palette.warning.main}`,
              boxShadow: 'none',
              bgcolor: 'background.paper',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
            >
              <Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 0.75 }}>
                  <Chip color="warning" label="Payment required" size="small" sx={{ fontWeight: 700 }} />
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    Complete payment to unlock your workspace
                  </Typography>
                </Stack>
                <Typography color="text.secondary" sx={{ maxWidth: 820 }}>
                  Your current Free Plan has limited access. Choose a lifetime plan below and complete payment to continue using all pages.
                </Typography>
              </Box>
            </Stack>
          </Card>
          <Box id="billing-payment-options">{paymentComponent}</Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{translate('billing.title')}</title>
      </Helmet>
      <Box>
        <Tabs
          value={currentTab}
          scrollButtons="auto"
          variant="scrollable"
          sx={{ gap: '0.5rem', minHeight: '37px' }}
          allowScrollButtonsMobile
          onChange={(e, value) => setCurrentTab(value)}
        >
          {ACCOUNT_TABS.map((tab) => {
            let isShow = !tab.hide && tab.permissions.some((b) => userPermissions[b]);
            return (
              isShow && (
                <Tab
                  disableRipple
                  key={tab.value}
                  label={isMobile ? '' : tab.label}
                  icon={tab.icon}
                  value={tab.value}
                  sx={{
                    flexDirection: 'row',
                    gap: '0.25rem',
                    padding: 0,
                    minHeight: '10px',
                    mr: '0.75rem',
                    fontWeight: 600,
                  }}
                />
              )
            );
          })}
        </Tabs>
      </Box>
      <Box sx={{ mb: 2 }} />
      {ACCOUNT_TABS.map((tab) => {
        let isShow = !tab.hide && tab.permissions.some((b) => userPermissions[b]);
        const isMatched = tab.value === currentTab;
        return isShow && isMatched && <Box key={tab.value}>{tab.component}</Box>;
      })}
    </>
  );
};
export default BillingInfoComponent;
